import api from './api';
import {
  InitPaymentResponse,
  SubscriptionPlan,
  SubscriptionStatus,
  TransactionsResponse,
  CancelSubscriptionResponse,
  AdminSubscriptionsResponse,
  AdminTransactionsResponse,
  WebhookLogsResponse,
  UpdatePlanRequest,
  PlansResponse,
  Transaction,
  AdminSubscription,
  WebhookLog,
  PaymentOptionsResponse
} from '../types/payment';
import { unwrapApiResponse, ApiResponse } from '@/utils/api-response.util';
import { PAYMENT_CONFIG } from '../config';

// Client API (User context)
export const getSubscriptionPlans = async (): Promise<PlansResponse> => {
  // We need custom handling here because 'links' is outside 'data'
  const response = await api.get<ApiResponse<SubscriptionPlan[]> & { links?: { publicOffer?: string } }>('/client-payments/plans');
  
  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to fetch plans');
  }

  return {
    plans: response.data.data,
    links: response.data.links
  };
};

export const getSubscriptionStatus = async (): Promise<SubscriptionStatus> => {
  const response = await api.get<ApiResponse<SubscriptionStatus>>('/client-payments/my-subscription');
  return unwrapApiResponse(response);
};

export const initPayment = async (planId: number, billingCycle: 'month' | 'year' = 'month'): Promise<InitPaymentResponse> => {
  const headers: Record<string, string> = {};
  if (PAYMENT_CONFIG.enableSandbox) {
    headers['X-Payment-Sandbox'] = 'true';
  }
  
  const response = await api.post<ApiResponse<InitPaymentResponse>>(
    '/client-payments/init', 
    { planId, billingCycle },
    { headers }
  );
  return unwrapApiResponse(response);
};

// Send payment callback manually (fallback for local dev or unreliable webhooks)
export const sendPaymentCallback = async (paymentData: any): Promise<any> => {
  const response = await api.post<ApiResponse<any>>('/client-payments/callback', paymentData);
  return unwrapApiResponse(response);
};

// Alias for backward compatibility if needed, but initPayment is preferred
export const initSubscription = initPayment;

export const cancelSubscription = async (): Promise<CancelSubscriptionResponse> => {
  const response = await api.post<ApiResponse<CancelSubscriptionResponse>>('/client-payments/cancel');
  return unwrapApiResponse(response);
};

export const getTransactions = async (limit = 20, offset = 0, status?: string, type?: string): Promise<TransactionsResponse> => {
  const params: any = { limit, offset };
  if (status) params.status = status;
  if (type) params.type = type;

  const response = await api.get<ApiResponse<Transaction[]>>('/client-payments/my-transactions', {
    params,
  });
  
  // Manual unwrap to handle pagination meta
  const { data, meta } = response.data;
  
  return {
    items: data,
    total: meta?.total || 0,
    limit: meta?.limit || limit,
    offset: meta?.offset || offset
  };
};

export const getPaymentOptions = async (): Promise<PaymentOptionsResponse> => {
  const response = await api.get<ApiResponse<PaymentOptionsResponse>>('/client-payments/options');
  return unwrapApiResponse(response);
};

// Admin API (Superadmin context)
export const getAdminPlans = async (includeInactive = false): Promise<SubscriptionPlan[]> => {
  const response = await api.get<ApiResponse<SubscriptionPlan[]>>('/admin/client-payments/plans', {
    params: { includeInactive },
  });
  return unwrapApiResponse(response);
};

export const updateAdminPlan = async (id: number, data: UpdatePlanRequest): Promise<SubscriptionPlan> => {
  const response = await api.patch<ApiResponse<SubscriptionPlan>>(`/admin/client-payments/plans/${id}`, data);
  return unwrapApiResponse(response);
};

export const getAdminExpertPlans = async (includeInactive = false): Promise<SubscriptionPlan[]> => {
  const response = await api.get<ApiResponse<SubscriptionPlan[]>>('/admin/expert-payments/plans', {
    params: { includeInactive },
  });
  return unwrapApiResponse(response);
};

export const updateAdminExpertPlan = async (id: number, data: UpdatePlanRequest): Promise<SubscriptionPlan> => {
  const response = await api.patch<ApiResponse<SubscriptionPlan>>(`/admin/expert-payments/plans/${id}`, data);
  return unwrapApiResponse(response);
};

export const getAdminExpertSubscriptions = async (
  limit = 20,
  offset = 0,
  status?: string,
  userId?: number,
): Promise<AdminSubscriptionsResponse> => {
  const params: any = { limit, offset };
  if (status) params.status = status;
  if (userId) params.userId = userId;

  const response = await api.get<ApiResponse<AdminSubscription[]>>('/admin/expert-payments/subscriptions', { params });

  const { data, meta } = response.data;
  return {
    items: data,
    total: meta?.total || 0,
    limit: meta?.limit || limit,
    offset: meta?.offset || offset,
  };
};

export const getAdminExpertTransactions = async (
  limit = 20,
  offset = 0,
  status?: string,
  userId?: number,
): Promise<AdminTransactionsResponse> => {
  const params: any = { limit, offset };
  if (status) params.status = status;
  if (userId) params.userId = userId;

  const response = await api.get<ApiResponse<Transaction[]>>('/admin/expert-payments/transactions', { params });

  const { data, meta } = response.data;
  return {
    items: data,
    total: meta?.total || 0,
    limit: meta?.limit || limit,
    offset: meta?.offset || offset,
  };
};

export const getAdminSubscriptions = async (
  limit = 20,
  offset = 0,
  status?: string,
  userId?: number
): Promise<AdminSubscriptionsResponse> => {
  const params: any = { limit, offset };
  if (status) params.status = status;
  if (userId) params.userId = userId;

  const response = await api.get<ApiResponse<AdminSubscription[]>>('/admin/client-payments/subscriptions', {
    params,
  });
  
  // Manual unwrap to handle pagination meta
  const { data, meta } = response.data;
  
  return {
    items: data,
    total: meta?.total || 0,
    limit: meta?.limit || limit,
    offset: meta?.offset || offset
  };
};

export const getAdminTransactions = async (
  limit = 20,
  offset = 0,
  status?: string,
  userId?: number
): Promise<AdminTransactionsResponse> => {
  const params: any = { limit, offset };
  if (status) params.status = status;
  if (userId) params.userId = userId;

  const response = await api.get<ApiResponse<Transaction[]>>('/admin/client-payments/transactions', {
    params,
  });
  
  // Manual unwrap to handle pagination meta
  const { data, meta } = response.data;
  
  return {
    items: data,
    total: meta?.total || 0,
    limit: meta?.limit || limit,
    offset: meta?.offset || offset
  };
};

export const getWebhookLogs = async (
  limit = 20,
  offset = 0,
  orderReference?: string
): Promise<WebhookLogsResponse> => {
  const params: any = { limit, offset };
  if (orderReference) params.orderReference = orderReference;

  const response = await api.get<ApiResponse<WebhookLog[]>>('/admin/client-payments/webhook-logs', {
    params,
  });

  // Manual unwrap to handle pagination meta
  const { data, meta } = response.data;

  return {
    items: data,
    total: meta?.total || 0,
    limit: meta?.limit || limit,
    offset: meta?.offset || offset
  };
};

export const getAdminExpertWebhookLogs = async (
  limit = 20,
  offset = 0,
  orderReference?: string
): Promise<WebhookLogsResponse> => {
  const params: any = { limit, offset };
  if (orderReference) params.orderReference = orderReference;

  const response = await api.get<ApiResponse<WebhookLog[]>>('/admin/expert-payments/webhook-logs', {
    params,
  });

  const { data, meta } = response.data;

  return {
    items: data,
    total: meta?.total || 0,
    limit: meta?.limit || limit,
    offset: meta?.offset || offset
  };
};

// Dev Tools
export const devSetStatus = async (
  userId: number,
  status: string,
  days: number,
  isTrial: boolean = false,
  billingCycle: 'month' | 'year' = 'month'
) => {
  await api.post('/client-payments/dev/set-status', {
    userId,
    status,
    days,
    isTrial,
    billingCycle
  });
};

export const expertDevSetStatus = async (
  userId: number,
  status: string,
  days: number,
  isTrial: boolean = false,
  billingCycle: 'month' | 'year' = 'month'
) => {
  await api.post('/expert-payments/dev/set-status', {
    userId,
    status,
    days,
    isTrial,
    billingCycle
  });
};

export const paymentService = {
  getPlans: getSubscriptionPlans,
  getMySubscription: getSubscriptionStatus,
  initPayment,
  sendPaymentCallback, // Export new method
  initSubscription, // Keep for compatibility
  cancelSubscription,
  getMyTransactions: getTransactions,
  getPaymentOptions,
  getAdminPlans,
  updateAdminPlan,
  getAdminSubscriptions,
  getAdminTransactions,
  getWebhookLogs,
  getAdminExpertWebhookLogs,
  devSetStatus,
  expertDevSetStatus
};
